<template>
    <Dialog 
        :visible.sync="visible" 
        :modal="true" 
        :style="{width: '450px'}"
        dismissableMask
        header="Invite Team Member to Channel"
        @hide="closeDialog"
    >
        <div class="flex flex-column gap-3">
            <div class="field">
                <label for="teamMember" class="font-medium">Select Team Member</label>
                <Dropdown
                    id="teamMember"
                    v-model="selectedUser"
                    :options="availableUsers"
                    optionLabel="displayName"
                    optionValue="id"
                    dataKey="id"
                    placeholder="Choose a team member to invite"
                    class="w-full"
                    filter
                    :loading="loading"
                />
            </div>
            
            <div class="field">
                <label for="inviteMessage" class="font-medium">Message (Optional)</label>
                <Textarea
                    id="inviteMessage"
                    v-model="inviteMessage"
                    placeholder="Add a message explaining why you're inviting this team member..."
                    rows="3"
                    class="w-full"
                />
            </div>
        </div>

        <template #footer>
            <Button 
                label="Cancel" 
                icon="pi pi-times" 
                @click="closeDialog" 
                class="p-button-text" 
            />
            <Button 
                label="Send Invite" 
                icon="pi pi-send" 
                @click="sendInvite" 
                :disabled="!selectedUser || sending"
                :loading="sending"
            />
        </template>
    </Dialog>
</template>

<script>
import { ref, computed, watch, onMounted } from '@vue/composition-api'
import Dialog from 'primevue/dialog'
import Dropdown from 'primevue/dropdown'
import Textarea from 'primevue/textarea'
import Button from 'primevue/button'

export default {
    name: 'ChatInviteDialog',
    components: {
        Dialog,
        Dropdown,
        Textarea,
        Button,
    },
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        channel: {
            type: Object,
            default: null,
        },
    },
    created() {
        console.log('ChatInviteDialog: created() lifecycle hook called')
        console.log('ChatInviteDialog: initial props:', this.$props)
    },
    mounted() {
        console.log('ChatInviteDialog: mounted() lifecycle hook called')
        console.log('ChatInviteDialog: mounted props:', this.$props)
    },
    setup(props, { root, emit }) {
        console.log('ChatInviteDialog: setup() called with props:', props)
        
        const visible = ref(false)
        const selectedUser = ref(null)
        const inviteMessage = ref('')
        const loading = ref(false)
        const sending = ref(false)

        // Store getters
        const userId = computed(() => root.$store.getters['portal/userId'])
        const currentSite = computed(() => root.$store.getters['portal/getCurrentSite'])
        const portalUser = computed(() => root.$store.state.portal.portalUser)
        
        // Fetch site users when dialog opens
        const fetchSiteUsers = async () => {
            if (!props.channel?.tenants?.length) return []
            
            loading.value = true
            try {
                // Fetch users for the channel's tenant sites
                const siteUsers = await root.$apiv2.getSiteUsers(props.channel.tenants)
                console.log('ChatInviteDialog: Fetched site users:', siteUsers)
                return siteUsers || []
            } catch (error) {
                console.error('ChatInviteDialog: Error fetching site users:', error)
                return []
            } finally {
                loading.value = false
            }
        }

        // Site users (fetched when dialog opens)
        const siteUsers = ref([])

        // Get available users (exclude current user and existing participants)
        const availableUsers = computed(() => {
            console.log('ChatInviteDialog: Computing available users from siteUsers:', siteUsers.value)
            
            if (!Array.isArray(siteUsers.value) || !siteUsers.value.length) {
                console.log('ChatInviteDialog: No site users found')
                return []
            }

            if (!props.channel) {
                console.log('ChatInviteDialog: No channel prop')
                return []
            }

            const existingParticipantIds = new Set(
                props.channel?.participants?.map(p => p.principal) || []
            )
            console.log('ChatInviteDialog: Existing participant IDs:', Array.from(existingParticipantIds))

            try {
                const filteredUsers = siteUsers.value
                    .filter(user => {
                        // Ensure user object has required properties
                        if (!user || !user.id) return false
                        // Exclude current user and existing participants
                        return user.id !== userId.value && !existingParticipantIds.has(user.id)
                    })
                    .map(user => {
                        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim()
                        // Use email or ID as fallback to ensure unique displayName
                        const displayName = fullName || user.email || `User ${user.id}`
                        return {
                            id: user.id,
                            displayName,
                            firstName: user.firstName,
                            lastName: user.lastName,
                            email: user.email,
                        }
                    })
                    .sort((a, b) => a.displayName.localeCompare(b.displayName))
                
                console.log('ChatInviteDialog: Filtered users count:', filteredUsers.length)
                return filteredUsers
            } catch (error) {
                console.error('ChatInviteDialog: Error computing available users:', error)
                return []
            }
        })

        const closeDialog = () => {
            visible.value = false
            // Delay clearing data to prevent issues during dialog close animation
            setTimeout(() => {
                selectedUser.value = null
                inviteMessage.value = ''
            }, 300)
            emit('close')
        }

        const sendInvite = async () => {
            if (!selectedUser.value || !props.channel) return

            sending.value = true

            try {
                const selectedUserData = availableUsers.value.find(u => u.id === selectedUser.value)
                
                await root.$store.dispatch('messaging/inviteToChannel', {
                    channel: props.channel,
                    userId: selectedUser.value,
                    message: inviteMessage.value.trim() || undefined,
                })

                root.$toast.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: `${selectedUserData?.displayName || 'Team member'} invited successfully`,
                    life: 3000
                })
                
                // Emit event to add system message to thread
                emit('userInvited', {
                    userName: selectedUserData?.displayName || 'Team member',
                    message: inviteMessage.value.trim()
                })
                
                closeDialog()
            } catch (error) {
                console.error('Error inviting team member:', error)
                root.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to invite team member',
                    life: 3000
                })
            } finally {
                sending.value = false
            }
        }

        // Initialize visible with show prop
        onMounted(() => {
            console.log('ChatInviteDialog: Component mounted, show prop:', props.show)
            if (props.show) {
                visible.value = true
            }
        })

        // Watch for show prop changes
        watch(
            () => props.show,
            async (newValue, oldValue) => {
                console.log('ChatInviteDialog: show prop changed from', oldValue, 'to', newValue)
                console.log('ChatInviteDialog: channel prop:', props.channel?.patientFullName)
                visible.value = newValue
                if (newValue && !oldValue) {
                    // Reset form when dialog opens
                    selectedUser.value = null
                    inviteMessage.value = ''
                    // Fetch site users when dialog opens
                    siteUsers.value = await fetchSiteUsers()
                    console.log('ChatInviteDialog: Dialog opened, fetched users:', siteUsers.value.length)
                    console.log('ChatInviteDialog: Available users count:', availableUsers.value?.length)
                }
            },
            { immediate: true }
        )

        // Watch for visible changes to emit close event when dialog is closed via X or dismissableMask
        watch(visible, (newValue) => {
            if (!newValue && props.show) {
                emit('close')
            }
        })

        return {
            visible,
            selectedUser,
            inviteMessage,
            loading,
            sending,
            siteUsers,
            availableUsers,
            closeDialog,
            sendInvite,
        }
    },
}
</script>

<style lang="scss" scoped>
.field {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

::v-deep .p-dialog-content {
    padding: 1.5rem;
}

::v-deep .p-dialog-footer {
    padding: 1rem 1.5rem;
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
}
</style>
